<template>
  <v-dialog v-model="showDialog" width="450" persistent>
    <v-card>
      <v-form ref="form" validate-on="invalid-input lazy" @submit.prevent="save">
        <v-card-title>{{
          group ? t("categories.manage.editGroupTitle") : t("categories.manage.newGroupTitle")
        }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.trim="name"
            :rules="[required, uniqueName]"
            variant="outlined"
            autofocus
          >
            <template #label>
              {{ t("categories.manage.groupNamePrompt") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDialog = false">{{ t("common.cancel") }}</v-btn>
          <v-btn variant="flat" type="submit" color="primary">{{ t("common.save") }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  group: { id: string; name: string } | null;
  existingGroups: Array<{ id: string; name: string }>;
}>();
const emit = defineEmits<{ save: [value: { name: string }] }>();

const showDialog = defineModel<boolean>({ required: true });

const { t } = useI18n();
const { required } = useRules();
const form = ref<VForm>();

const name = ref("");

watch(
  () => [showDialog.value, props.group] as const,
  ([open, group]) => {
    if (open) name.value = group?.name ?? "";
  },
);

const uniqueName = computed(() => {
  return (value: string) => {
    if (!value) return true;
    const normalized = value.toLowerCase().trim();
    const conflict = props.existingGroups.find(
      (g) => g.name.toLowerCase().trim() === normalized && g.id !== props.group?.id,
    );
    return !conflict || t("categories.manage.groupAlreadyExists");
  };
});

const save = async () => {
  const valid = await form.value?.validate();
  if (!valid?.valid) return;
  emit("save", { name: name.value });
  showDialog.value = false;
};
</script>
